// ===================================
// TRENCH QUEST - Dynamic Actions Prompt
// AI-generated contextual idle actions
// ===================================

import type { GameSession, DynamicAction } from '@/types'

/**
 * System prompt for generating dynamic actions
 */
export const DYNAMIC_ACTIONS_SYSTEM_PROMPT = `You are generating contextual idle actions for TRENCH QUEST, a satirical crypto trading game.

Generate 3-4 short, contextual actions the player can take based on their current state. These replace static "WAIT" buttons with dynamic, story-relevant options.

## ACTION CATEGORIES

- **explore**: Looking around, investigating, scouting
- **wait**: Passing time, watching charts, doom-scrolling
- **social**: Networking, chatting, meeting people
- **trade**: Trading-related activities (not actual trades)
- **rest**: Taking breaks, sleeping, self-care

## CONTEXT AWARENESS

Consider:
1. **Time of day** - Night actions differ from morning
2. **Location** - Actions should fit where they are
3. **Portfolio state** - If holding a token, suggest checking on it
4. **Recent events** - Callbacks to past situations
5. **Player traits** - High degen = degen options

## OUTPUT FORMAT

Return a JSON array of 3-4 actions:

\`\`\`json
[
  {
    "id": "action-unique-id",
    "label": "Short button text (max 25 chars)",
    "description": "What this action does and implies",
    "category": "explore|wait|social|trade|rest",
    "gameHoursSkip": 1,
    "triggerContext": "Context for AI if selected - what happens"
  }
]
\`\`\`

## EXAMPLES

Morning, holding BONK:
- "Check BONK Discord" (social, 1hr) - See what the community is saying
- "Scroll CT for alpha" (wait, 1hr) - Doom-scroll for opportunities
- "Grab coffee" (rest, 0.5hr) - Start the day right

Night, all cash:
- "Browse pump.fun" (explore, 1hr) - Look for new launches
- "Take a power nap" (rest, 4hr) - Rest up for tomorrow
- "Join a Discord raid" (social, 2hr) - Network with degens

After a big win:
- "Post W to CT" (social, 0.5hr) - Flex on the timeline
- "Research next play" (trade, 2hr) - Don't get complacent
- "Touch grass" (rest, 1hr) - Remember the outside world exists

## TONE

Keep it fun, degen, self-aware. These are quick actions, not major events.
CT slang encouraged: "doom-scroll", "touch grass", "check the charts", etc.`

/**
 * Build prompt for generating dynamic actions
 */
export function buildDynamicActionsPrompt(session: GameSession): string {
  const { gameTime, world, player } = session
  const { portfolio, traits } = player

  // Time of day
  const hour = gameTime.gameHour
  const timeOfDay = hour < 6 ? 'late night' :
                    hour < 12 ? 'morning' :
                    hour < 17 ? 'afternoon' :
                    hour < 21 ? 'evening' : 'night'

  // Portfolio summary
  const holdings = portfolio.holdings.filter(h => h.symbol !== 'USDC')
  const holdingSummary = holdings.length === 0
    ? 'All in cash (USDC)'
    : holdings.map(h => {
        const pnl = ((h.currentPrice - h.avgBuyPrice) / h.avgBuyPrice) * 100
        return `${h.symbol} (${pnl >= 0 ? '+' : ''}${pnl.toFixed(1)}%)`
      }).join(', ')

  // Trait summary
  const traitSummary = `Degen: ${traits.degenScore}, Trust: ${traits.trustScore}`

  return `${DYNAMIC_ACTIONS_SYSTEM_PROMPT}

---

# CURRENT CONTEXT

Time: Day ${gameTime.gameDay}, ${timeOfDay} (${hour}:00)
Location: ${world.currentLocation}
Holdings: ${holdingSummary}
Cash: $${portfolio.cashBalance.toLocaleString()}
Traits: ${traitSummary}
Recent News: ${world.recentNews.slice(0, 2).join('; ')}

---

Generate 3-4 contextual actions for this player right now.
Make them relevant to their time, location, and holdings.
${holdings.length > 0 ? `Include at least one action related to their ${holdings[0]?.symbol} position.` : 'Include actions that might help them find their next trade.'}

Output valid JSON array only, no additional text.`
}

/**
 * Default actions when AI generation fails
 */
export function getDefaultDynamicActions(session: GameSession): DynamicAction[] {
  const hour = session.gameTime.gameHour
  const holdings = session.player.portfolio.holdings.filter(h => h.symbol !== 'USDC')

  const actions: DynamicAction[] = []

  // Time-based action
  if (hour >= 22 || hour < 6) {
    actions.push({
      id: 'sleep',
      label: 'Get some sleep',
      description: 'Rest up - the markets will still be here tomorrow',
      category: 'rest',
      gameHoursSkip: 6,
      triggerContext: 'Player rested for 6 hours',
    })
  } else {
    actions.push({
      id: 'wait',
      label: 'Watch the charts',
      description: 'Stare at candles and wait for something to happen',
      category: 'wait',
      gameHoursSkip: 1,
      triggerContext: 'Player watched charts for an hour',
    })
  }

  // Scroll action
  actions.push({
    id: 'scroll-ct',
    label: 'Scroll CT',
    description: 'Doom-scroll Crypto Twitter for alpha or entertainment',
    category: 'wait',
    gameHoursSkip: 1,
    triggerContext: 'Player scrolled through Crypto Twitter',
  })

  // Holdings-based action
  if (holdings.length > 0 && holdings[0]) {
    const symbol = holdings[0].symbol
    actions.push({
      id: `check-${symbol.toLowerCase()}`,
      label: `Check ${symbol} chat`,
      description: `See what the ${symbol} community is saying`,
      category: 'social',
      gameHoursSkip: 1,
      triggerContext: `Player checked ${symbol} Discord/Telegram`,
    })
  } else {
    actions.push({
      id: 'browse-new',
      label: 'Browse new launches',
      description: 'Check pump.fun for fresh opportunities',
      category: 'explore',
      gameHoursSkip: 1,
      triggerContext: 'Player browsed pump.fun for new tokens',
    })
  }

  // Social action
  actions.push({
    id: 'network',
    label: 'Touch grass',
    description: 'Step away from the screen for a bit',
    category: 'rest',
    gameHoursSkip: 2,
    triggerContext: 'Player took a break from trading',
  })

  return actions.slice(0, 4)
}

/**
 * Parse AI response into DynamicAction array
 */
export function parseDynamicActionsResponse(response: string): DynamicAction[] {
  try {
    // Clean up response
    let cleaned = response.trim()

    // Remove markdown code blocks if present
    if (cleaned.startsWith('```')) {
      cleaned = cleaned.replace(/```json\n?/g, '').replace(/```\n?/g, '')
    }

    const parsed = JSON.parse(cleaned)

    if (!Array.isArray(parsed)) {
      throw new Error('Response is not an array')
    }

    // Validate and clean each action
    return parsed.slice(0, 4).map((action, index) => ({
      id: action.id || `action-${Date.now()}-${index}`,
      label: String(action.label || 'Do something').slice(0, 25),
      description: String(action.description || ''),
      category: validateCategory(action.category),
      gameHoursSkip: Math.max(0, Math.min(8, Number(action.gameHoursSkip) || 1)),
      triggerContext: action.triggerContext,
    }))
  } catch (error) {
    console.error('Failed to parse dynamic actions:', error)
    return []
  }
}

function validateCategory(cat: unknown): DynamicAction['category'] {
  const valid = ['explore', 'wait', 'social', 'trade', 'rest']
  if (typeof cat === 'string' && valid.includes(cat)) {
    return cat as DynamicAction['category']
  }
  return 'wait'
}
